import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import FormField from '../ui/FormField';
import { ChevronDown, ChevronUp, Settings, Users, X, RotateCcw, DollarSign, Clock } from 'lucide-react';
import { useProfessionals } from '../../contexts/ProfessionalContext';
import { toggleProfessionalServiceCustomization } from '../../firebase/professionalServices';
import { useAuth } from '../../contexts/AuthContext';
import { getSalonIdFromService } from '../../firebase/services';

// Componente Switch para ativar/desativar personalizações
const Switch = ({ checked, onChange, label, id }) => {
  return (
    <div className="flex items-center justify-between">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">
        {label}
      </label>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        id={id}
        onClick={onChange}
        className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
          checked ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span className="sr-only">{label}</span>
        <span
          aria-hidden="true"
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
};

/**
 * Modal para adicionar ou editar serviços
 * 
 * @param {boolean} isOpen - Controla se o modal está aberto
 * @param {function} onClose - Função chamada ao fechar o modal
 * @param {object} service - Serviço a ser editado (null para criar novo)
 * @param {array} categories - Lista de categorias disponíveis
 * @param {function} onSave - Função chamada ao salvar o serviço
 * @param {boolean} isSaving - Indica se está salvando (para desabilitar botões)
 */
const ServiceModal = ({ isOpen, onClose, service, categories, onSave, isSaving = false }) => {
  // Estados para os campos do formulário
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [preco, setPreco] = useState('0');
  const [duracao, setDuracao] = useState('30');
  const [categoria, setCategoria] = useState('');
  const [setupTime, setSetupTime] = useState('0');
  const [finishTime, setFinishTime] = useState('0');
  const [allowsOverlap, setAllowsOverlap] = useState(false);
  
  // Estado para controlar a visibilidade das opções avançadas
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Estado para erros do formulário
  const [error, setError] = useState(null);

  // Estado para controlar a aba ativa
  const [activeTab, setActiveTab] = useState('basic');

  // Hook para buscar profissionais
  const { professionals, loading: professionalsLoading } = useProfessionals();

  // Estado para preços e durações customizados por profissional
  const [professionalCustomizations, setProfessionalCustomizations] = useState({});
  
  // Estado para controlar quais campos foram modificados para feedback visual
  const [recentlyCleared, setRecentlyCleared] = useState({});

  // IMPORTANTE: Hooks devem ser chamados incondicionalmente
  // Se este componente for usado em um ambiente onde AuthContext não existe,
  // certifique-se de que o useAuth retorne pelo menos um objeto vazio
  const authContext = useAuth();
  
  // Função auxiliar para obter salonId com segurança
  const getSalonId = () => {
    // Tentar obter do serviço
    if (service && service.salonId) {
      console.log(`[getSalonId] Usando salonId do serviço: ${service.salonId}`);
      return service.salonId;
    }
    
    // Tentar obter do contexto de autenticação
    if (authContext && authContext.salonId) {
      console.log(`[getSalonId] Usando salonId do contexto de autenticação: ${authContext.salonId}`);
      return authContext.salonId;
    }
    
    if (authContext && authContext.user && authContext.user.salonId) {
      console.log(`[getSalonId] Usando salonId do usuário autenticado: ${authContext.user.salonId}`);
      return authContext.user.salonId;
    }
    
    // Tentar obter de variáveis globais
    if (window.salonId) {
      console.log(`[getSalonId] Usando salonId global: ${window.salonId}`);
      return window.salonId;
    }
    
    if (window.salon && window.salon.id) {
      console.log(`[getSalonId] Usando ID do salão global: ${window.salon.id}`);
      return window.salon.id;
    }
    
    // Tentar obter dos parâmetros da URL - verificar common patterns
    const urlMatches = [
      // Formato: /salons/{salonId}/...
      window.location.pathname.match(/\/salons\/([^\/]+)/),
      // Formato: /salon/{salonId}/...
      window.location.pathname.match(/\/salon\/([^\/]+)/),
      // Formato: /admin/{salonId}/...
      window.location.pathname.match(/\/admin\/([^\/]+)\//)
    ];
    
    for (const match of urlMatches) {
      if (match && match[1]) {
        console.log(`[getSalonId] Extraído da URL: ${match[1]}`);
        return match[1];
      }
    }
    
    // Tentar obter do localStorage como último recurso
    if (localStorage.getItem('salonId')) {
      const savedId = localStorage.getItem('salonId');
      console.log(`[getSalonId] Usando salonId do localStorage: ${savedId}`);
      return savedId;
    }
    
    // Evitar usar "default" - se chegarmos aqui, é melhor não definir um valor padrão
    console.warn('[getSalonId] ALERTA: Não foi possível determinar salonId!');
    return null;
  };

  // Preencher o formulário quando o serviço muda
  useEffect(() => {
    if (service) {
      setNome(service.nome || '');
      setDescricao(service.descricao || '');
      setPreco(service.preco?.toString() || '0');
      setDuracao(service.duracao?.toString() || '30');
      setCategoria(service.categoria || service.categoryId || '');
      setSetupTime(service.setupTime?.toString() || '0');
      setFinishTime(service.finishTime?.toString() || '0');
      setAllowsOverlap(service.allowsOverlap || false);
      
      // Carregar customizações existentes, incluindo rascunhos
      if (service.professionalCustomizations) {
        setProfessionalCustomizations(service.professionalCustomizations);
      }
    } else {
      // Resetar formulário para novo serviço
      setNome('');
      setDescricao('');
      setPreco('0');
      setDuracao('30');
      setCategoria('');
      setSetupTime('0');
      setFinishTime('0');
      setAllowsOverlap(false);
      setProfessionalCustomizations({});
    }
    
    // Limpar feedback visual
    setRecentlyCleared({});
  }, [service]);

  // Função para sincronizar o estado do switch com o Firebase (chamada quando serviço for salvo)
  const syncProfessionalServiceCustomizations = async (salonId, serviceId) => {
    if (!salonId || !serviceId) {
      console.error(`[syncProfessionalServiceCustomizations] IDs inválidos: salonId=${salonId}, serviceId=${serviceId}`);
      return;
    }
    
    try {
      console.log(`[syncProfessionalServiceCustomizations] Iniciando sincronização para salão=${salonId}, serviço=${serviceId}`);
      
      // Criar cópia do objeto para evitar modificações durante o processamento
      const customizationsCopy = JSON.parse(JSON.stringify(professionalCustomizations));
      
      // Processar cada profissional
      const syncPromises = Object.entries(customizationsCopy).map(async ([profId, customization]) => {
        // Verificar estado do switch baseado na presença de valores customizados
        const isActive = hasProfessionalCustomization(profId);
        
        console.log(`[syncProfessionalServiceCustomizations] Profissional=${profId}, isActive=${isActive}`);
        console.log(`[syncProfessionalServiceCustomizations] Dados atuais:`, customization);
        
        // Chamar a função centralizada do Firebase com force=true para garantir a atualização
        try {
          const result = await toggleProfessionalServiceCustomization(
            salonId,
            profId,
            serviceId,
            isActive,
            {
              defaultPrice: Number(preco),
              defaultDuration: Number(duracao)
            }
          );
          
          console.log(`[syncProfessionalServiceCustomizations] Resultado para ${profId}:`, result);
          return result;
        } catch (err) {
          console.error(`[syncProfessionalServiceCustomizations] Erro ao sincronizar ${profId}:`, err);
          throw err;
        }
      });
      
      // Aguardar todas as operações assíncronas
      const results = await Promise.all(syncPromises);
      console.log(`[syncProfessionalServiceCustomizations] Sincronização concluída para ${results.length} profissionais`);
      
    } catch (error) {
      console.error(`[syncProfessionalServiceCustomizations] Erro na sincronização:`, error);
      throw error;
    }
  };

  // Handler para salvar o formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validações básicas
    if (!nome.trim()) {
      setError('Nome do serviço é obrigatório');
      return;
    }

    if (!categoria) {
      setError('Categoria é obrigatória');
      return;
    }

    // Processar customizações - Remover campos vazios
    const cleanedCustomizations = {};
    Object.entries(professionalCustomizations).forEach(([profId, customization]) => {
      const { customPrice, customDuration, draftPrice, draftDuration } = customization;
      
      // Incluir tanto valores ativos quanto rascunhos
      if (customPrice !== null || customDuration !== null || draftPrice !== null || draftDuration !== null) {
        cleanedCustomizations[profId] = {
          customPrice: customPrice === '' ? null : customPrice,
          customDuration: customDuration === '' ? null : customDuration,
          draftPrice: draftPrice === '' ? null : draftPrice,
          draftDuration: draftDuration === '' ? null : draftDuration
        };
      }
    });

    // Converter valores numéricos
    const serviceData = {
      nome: nome.trim(),
      descricao: descricao.trim(),
      preco: Number(preco) || 0,
      duracao: Number(duracao) || 30,
      categoria,
      setupTime: Number(setupTime) || 0,
      finishTime: Number(finishTime) || 0,
      allowsOverlap,
      professionalCustomizations: Object.keys(cleanedCustomizations).length > 0 
        ? cleanedCustomizations 
        : undefined
    };

    try {
      // Salvar o serviço
      const savedService = await onSave(serviceData);
      console.log("[handleSubmit] Serviço salvo:", savedService);
      
      // Obter informações do serviço - usar o objeto salvado ou o objeto de edição
      const serviceInfo = savedService || service;
      console.log("[handleSubmit] Usando informações do serviço:", serviceInfo);
      
      // Se temos um ID de serviço, sincronizar customizações
      if (serviceInfo && serviceInfo.id) {
        // Obter salonId - verificar diferentes propriedades onde pode estar
        let salonId = null;
        
        // Opções onde o salonId pode estar armazenado
        if (serviceInfo.salonId) {
          salonId = serviceInfo.salonId;
          console.log(`[handleSubmit] Usando salonId do serviceInfo: ${salonId}`);
        } else if (serviceInfo.salon_id) {
          salonId = serviceInfo.salon_id;
          console.log(`[handleSubmit] Usando salon_id do serviceInfo: ${salonId}`);
        } else if (serviceInfo.salaoId) {
          salonId = serviceInfo.salaoId;
          console.log(`[handleSubmit] Usando salaoId do serviceInfo: ${salonId}`);
        } else if (serviceInfo.salao_id) {
          salonId = serviceInfo.salao_id;
          console.log(`[handleSubmit] Usando salao_id do serviceInfo: ${salonId}`);
        } else if (service && service.salonId) {
          salonId = service.salonId;
          console.log(`[handleSubmit] Usando salonId do service original: ${salonId}`);
        }
        
        // Se não encontrou, usar função auxiliar
        if (!salonId) {
          salonId = getSalonId();
          if (salonId) {
            console.log(`[handleSubmit] Usando salonId da função auxiliar: ${salonId}`);
          }
        }
        
        // Verificar se temos um valor válido para salonId
        // Rejeitar valores como "default", "undefined", "null", etc.
        if (!salonId || 
            salonId === "default" || 
            salonId === "undefined" || 
            salonId === "null" || 
            salonId.length <= 1) {
          
          console.warn(`[handleSubmit] salonId inválido encontrado: "${salonId}"`);
          console.log(`[handleSubmit] Tentando obter salonId do Firebase usando serviceId: ${serviceInfo.id}`);
          
          // Como último recurso, tentar buscar o salonId diretamente no Firebase
          try {
            salonId = await getSalonIdFromService(serviceInfo.id);
            if (salonId) {
              console.log(`[handleSubmit] salonId encontrado no Firebase: ${salonId}`);
            } else {
              console.error(`[handleSubmit] Não foi possível encontrar salonId no Firebase`);
            }
          } catch (lookupError) {
            console.error(`[handleSubmit] Erro ao buscar salonId no Firebase:`, lookupError);
          }
        }
        
        if (salonId && 
            salonId !== "default" && 
            salonId !== "undefined" && 
            salonId !== "null" && 
            salonId.length > 1) {
          
          console.log(`[handleSubmit] SalonId válido encontrado: ${salonId}`);
          console.log(`[handleSubmit] Iniciando sincronização manual para salão=${salonId}, serviço=${serviceInfo.id}`);
          
          // Para cada profissional, verificar se há customizações ativas
          for (const [professionalId, customization] of Object.entries(professionalCustomizations)) {
            const hasCustomPrice = hasProfessionalCustomization(professionalId);
            console.log('[handleSubmit] Atualizando manualmente: profId=' + professionalId + ', isActive=' + hasCustomPrice);
            
            if (hasCustomPrice) {
              // Importante: Usar os valores atualizados do state, não do serviço
              await toggleProfessionalServiceCustomization(
                salonId,
                professionalId,
                serviceInfo.id,
                true, // isActive = true
                {
                  // Usar os valores do state, não do serviço
                  defaultPrice: customization.customPrice,
                  defaultDuration: customization.customDuration
                }
              );
            }
          }
          
          console.log('[handleSubmit] Sincronização manual concluída');
        } else {
          console.error(`[handleSubmit] salonId inválido encontrado: "${salonId}"`);
          console.error("[handleSubmit] Não foi possível sincronizar - salonId inválido");
        }
      } else {
        console.error("[handleSubmit] Não foi possível sincronizar - serviceId não encontrado");
      }
    } catch (error) {
      console.error("[handleSubmit] Erro:", error);
      setError(error.message || 'Erro ao salvar serviço');
    }
  };

  // Handler para atualizar customização de profissional
  const handleProfessionalCustomization = (professionalId, field, value) => {
    setProfessionalCustomizations(prev => ({
      ...prev,
      [professionalId]: {
        ...prev[professionalId],
        [field]: value ? Number(value) : null
      }
    }));
    
    // Limpar o feedback visual quando o usuário edita novamente
    if (recentlyCleared[`${professionalId}-${field}`]) {
      setRecentlyCleared(prev => ({
        ...prev,
        [`${professionalId}-${field}`]: false
      }));
    }
  };
  
  // Handler para limpar um valor personalizado
  const handleClearCustomization = (professionalId, field) => {
    setProfessionalCustomizations(prev => ({
      ...prev,
      [professionalId]: {
        ...prev[professionalId],
        [field]: null
      }
    }));
    
    // Adicionar ao estado de recém-limpos para feedback visual
    setRecentlyCleared(prev => ({
      ...prev,
      [`${professionalId}-${field}`]: true
    }));
    
    // Remover o feedback após 2 segundos
    setTimeout(() => {
      setRecentlyCleared(prev => ({
        ...prev,
        [`${professionalId}-${field}`]: false
      }));
    }, 2000);
  };
  
  // Handler para resetar ambos valores (preço e duração) de um profissional
  const handleResetAllCustomizations = (professionalId) => {
    setProfessionalCustomizations(prev => ({
      ...prev,
      [professionalId]: {
        customPrice: null,
        customDuration: null
      }
    }));
    
    // Adicionar ambos ao estado de recém-limpos para feedback visual
    setRecentlyCleared(prev => ({
      ...prev,
      [`${professionalId}-customPrice`]: true,
      [`${professionalId}-customDuration`]: true
    }));
    
    // Remover o feedback após 2 segundos
    setTimeout(() => {
      setRecentlyCleared(prev => ({
        ...prev,
        [`${professionalId}-customPrice`]: false,
        [`${professionalId}-customDuration`]: false
      }));
    }, 2000);
  };

  // Verificar se algum campo do profissional tem valor personalizado
  const hasProfessionalCustomization = (professionalId) => {
    const customization = professionalCustomizations[professionalId];
    if (!customization) return false;
    
    // Verificar se há valores ativos (não nulos) nos campos de customização
    // Precisamos verificar estritamente com === null porque 0 é um valor válido
    const hasCustomPrice = customization.customPrice !== null && customization.customPrice !== undefined;
    const hasCustomDuration = customization.customDuration !== null && customization.customDuration !== undefined;
    
    // Log detalhado para depuração
    console.log(`[hasProfessionalCustomization] ${professionalId}: customPrice=${JSON.stringify(customization.customPrice)}, customDuration=${JSON.stringify(customization.customDuration)}`);
    console.log(`[hasProfessionalCustomization] ${professionalId}: price=${hasCustomPrice}, duration=${hasCustomDuration}`);
    
    // Ambos devem ser null/undefined para considerar desativado
    return hasCustomPrice || hasCustomDuration;
  };
  
  // Handler para alternar personalização (liga/desliga)
  const handleToggleCustomization = (professionalId) => {
    const hasCustom = hasProfessionalCustomization(professionalId);
    console.log(`[handleToggleCustomization] Profissional=${professionalId}, hasCustom=${hasCustom}`);
    
    if (hasCustom) {
      // Desativando: Guardar valores atuais como rascunho e limpar campos ativos
      const currentCustomization = professionalCustomizations[professionalId] || {};
      const currentPrice = currentCustomization.customPrice;
      const currentDuration = currentCustomization.customDuration;
      
      console.log(`[handleToggleCustomization] Desativando... Valores atuais:`, {
        customPrice: currentPrice,
        customDuration: currentDuration
      });
      
      // Importante: Use setState com callback para garantir estado atualizado
      setProfessionalCustomizations(prev => {
        const updatedState = {
          ...prev,
          [professionalId]: {
            ...prev[professionalId],
            draftPrice: currentPrice,
            draftDuration: currentDuration,
            // IMPORTANTE: Definir explicitamente como null
            customPrice: null,
            customDuration: null
          }
        };
        
        console.log(`[handleToggleCustomization] Estado atualizado:`, updatedState[professionalId]);
        return updatedState;
      });
    } else {
      // Ativando: Restaurar valores de rascunho ou usar valores padrão
      const currentCustomization = professionalCustomizations[professionalId] || {};
      const hasDraftPrice = currentCustomization.draftPrice !== undefined && currentCustomization.draftPrice !== null;
      const hasDraftDuration = currentCustomization.draftDuration !== undefined && currentCustomization.draftDuration !== null;
      
      console.log(`[handleToggleCustomization] Ativando... Rascunhos disponíveis:`, {
        draftPrice: hasDraftPrice ? currentCustomization.draftPrice : 'não disponível',
        draftDuration: hasDraftDuration ? currentCustomization.draftDuration : 'não disponível'
      });
      
      // Importante: Use setState com callback para garantir estado atualizado
      setProfessionalCustomizations(prev => {
        const updatedState = {
          ...prev,
          [professionalId]: {
            ...prev[professionalId],
            customPrice: hasDraftPrice 
              ? currentCustomization.draftPrice 
              : Number(preco),
            customDuration: hasDraftDuration 
              ? currentCustomization.draftDuration 
              : Number(duracao),
            draftPrice: null,
            draftDuration: null
          }
        };
        
        console.log(`[handleToggleCustomization] Estado atualizado:`, updatedState[professionalId]);
        return updatedState;
      });
    }
  };

  // Limpar o estado quando o modal é fechado
  const handleClose = () => {
    // Limpar todos os estados
    setProfessionalCustomizations({});
    setRecentlyCleared({});
    setError(null);
    
    // Chamar a função de fechamento original
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={service ? 'Editar Serviço' : 'Novo Serviço'}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              type="button"
              onClick={() => setActiveTab('basic')}
              className={`${
                activeTab === 'basic'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <Settings className="w-4 h-4 inline-block mr-1" />
              Básico
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('professionals')}
              className={`${
                activeTab === 'professionals'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              <Users className="w-4 h-4 inline-block mr-1" />
              Preços por Profissional
            </button>
          </nav>
        </div>

        {/* Conteúdo da aba básica */}
        {activeTab === 'basic' && (
          <div className="space-y-4">
            <FormField label="Nome do Serviço">
              <Input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Corte Feminino"
                required
              />
            </FormField>

            <FormField label="Descrição">
              <Input
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Descreva o serviço"
              />
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Preço">
                <Input
                  type="number"
                  value={preco}
                  onChange={(e) => setPreco(e.target.value)}
                  min="0"
                  step="0.01"
                  required
                />
              </FormField>

              <FormField label="Duração (minutos)">
                <Input
                  type="number"
                  value={duracao}
                  onChange={(e) => setDuracao(e.target.value)}
                  min="1"
                  required
                />
              </FormField>
            </div>

            <FormField label="Categoria">
              <select
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                required
              >
                <option value="">Selecione uma categoria</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nome}
                  </option>
                ))}
              </select>
            </FormField>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                {showAdvancedOptions ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Ocultar opções avançadas
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Mostrar opções avançadas
                  </>
                )}
              </button>
            </div>

            {showAdvancedOptions && (
              <div className="space-y-4 pt-4 border-t">
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Tempo de Preparo (minutos)">
                    <Input
                      type="number"
                      value={setupTime}
                      onChange={(e) => setSetupTime(e.target.value)}
                      min="0"
                    />
                  </FormField>

                  <FormField label="Tempo de Finalização (minutos)">
                    <Input
                      type="number"
                      value={finishTime}
                      onChange={(e) => setFinishTime(e.target.value)}
                      min="0"
                    />
                  </FormField>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="allowsOverlap"
                    checked={allowsOverlap}
                    onChange={(e) => setAllowsOverlap(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="allowsOverlap" className="ml-2 block text-sm text-gray-900">
                    Permitir sobreposição de horários
                  </label>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Conteúdo da aba de profissionais */}
        {activeTab === 'professionals' && (
          <div className="space-y-4">
            {/* Informações de referência para valores padrão */}
            <div className="bg-blue-50 rounded-md p-3 mb-4 text-sm text-blue-700 flex flex-col sm:flex-row sm:justify-between">
              <div className="mb-2 sm:mb-0">
                <span className="font-semibold">Preço padrão:</span> R$ {preco}
              </div>
              <div>
                <span className="font-semibold">Duração padrão:</span> {duracao} min
              </div>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Ative o switch para definir preços e durações personalizados para cada profissional.
            </p>

            {professionalsLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              </div>
            ) : professionals.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                Nenhum profissional cadastrado
              </div>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {professionals.map((professional) => {
                  const hasCustomization = hasProfessionalCustomization(professional.id);
                  
                  return (
                    <div 
                      key={professional.id} 
                      className={`rounded-lg border ${hasCustomization ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}
                    >
                      {/* Cabeçalho do card */}
                      <div className="flex justify-between items-center p-3 border-b border-gray-200">
                        <h4 className="font-medium text-gray-900">{professional.nome}</h4>
                        
                        {/* Switch para ativar/desativar personalização */}
                        <Switch 
                          id={`custom-toggle-${professional.id}`}
                          checked={hasCustomization}
                          onChange={() => handleToggleCustomization(professional.id)}
                          label="Personalizar"
                        />
                      </div>
                      
                      {/* Campos de entrada - só exibir quando customização está ativa */}
                      {hasCustomization && (
                        <div className="p-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Preço personalizado */}
                            <div className="flex items-start">
                              <div className="mt-1 mr-2 text-gray-500">
                                <DollarSign size={16} />
                              </div>
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Preço Personalizado
                                </label>
                                <div className="relative">
                                  <Input
                                    type="number"
                                    value={professionalCustomizations[professional.id]?.customPrice ?? ''}
                                    onChange={(e) => handleProfessionalCustomization(
                                      professional.id,
                                      'customPrice',
                                      e.target.value
                                    )}
                                    placeholder={`Padrão: R$ ${preco}`}
                                    min="0"
                                    step="0.01"
                                    className={`pr-8 ${recentlyCleared[`${professional.id}-customPrice`] ? 'bg-green-50 border-green-300' : ''}`}
                                  />
                                  {professionalCustomizations[professional.id]?.customPrice !== null && 
                                   professionalCustomizations[professional.id]?.customPrice !== undefined && (
                                    <button
                                      type="button"
                                      onClick={() => handleClearCustomization(professional.id, 'customPrice')}
                                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500"
                                      title="Limpar valor"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                                {recentlyCleared[`${professional.id}-customPrice`] && (
                                  <p className="text-xs text-green-600 mt-1 transition-opacity">
                                    Usando preço padrão
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Duração personalizada */}
                            <div className="flex items-start">
                              <div className="mt-1 mr-2 text-gray-500">
                                <Clock size={16} />
                              </div>
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Duração Personalizada
                                </label>
                                <div className="relative">
                                  <Input
                                    type="number"
                                    value={professionalCustomizations[professional.id]?.customDuration ?? ''}
                                    onChange={(e) => handleProfessionalCustomization(
                                      professional.id,
                                      'customDuration',
                                      e.target.value
                                    )}
                                    placeholder={`Padrão: ${duracao} min`}
                                    min="1"
                                    className={`pr-8 ${recentlyCleared[`${professional.id}-customDuration`] ? 'bg-green-50 border-green-300' : ''}`}
                                  />
                                  {professionalCustomizations[professional.id]?.customDuration !== null && 
                                   professionalCustomizations[professional.id]?.customDuration !== undefined && (
                                    <button
                                      type="button"
                                      onClick={() => handleClearCustomization(professional.id, 'customDuration')}
                                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500"
                                      title="Limpar valor"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                                {recentlyCleared[`${professional.id}-customDuration`] && (
                                  <p className="text-xs text-green-600 mt-1 transition-opacity">
                                    Usando duração padrão
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="text-red-600 text-sm">{error}</div>
        )}

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSaving}
          >
            {isSaving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ServiceModal; 